/**
 * Unified Auth Session Hook
 * 
 * Provides consistent auth state management across admin components.
 * Prevents race conditions between auth loading and data fetching.
 * 
 * Features:
 * - Session state with loading/authed/unauthed status
 * - Auto-refresh on tab focus/visibility change
 * - Proper cleanup and subscription management
 * - TypeScript-safe session access
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

type AuthStatus = 'loading' | 'authed' | 'unauthed';

interface AuthSessionState {
  status: AuthStatus;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
}

export function useAuthSession(): AuthSessionState {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession ?? null);
      setStatus(newSession ? 'authed' : 'unauthed');
    });

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('useAuthSession: Failed to get initial session', error);
          setStatus('unauthed');
          setSession(null);
          return;
        }
        
        setSession(data.session ?? null);
        setStatus(data.session ? 'authed' : 'unauthed');
      } catch (err) {
        console.error('useAuthSession: Error getting initial session', err);
        setStatus('unauthed');
        setSession(null);
      }
    };

    getInitialSession();

    // Refresh session on tab focus/visibility change to handle token expiry
    const handleFocusRefresh = async () => {
      // Only refresh if we think we're authenticated
      if (status === 'authed') {
        try {
          const { data } = await supabase.auth.getSession();
          setSession(data.session ?? null);
          setStatus(data.session ? 'authed' : 'unauthed');
        } catch (err) {
          console.warn('useAuthSession: Focus refresh failed', err);
        }
      }
    };

    // Listen for focus events
    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        handleFocusRefresh();
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleFocusRefresh);
    };
  }, [status]);

  return {
    status,
    session,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authed' && session !== null,
    accessToken: session?.access_token ?? null,
  };
}